import React, { useState, useEffect, useMemo } from 'react';
import { getAllFormResponses } from '../services/supabaseClient';
import { FiBarChart2, FiUsers, FiFileText, FiFilter, FiDownload, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiTrendingUp, FiMapPin, FiCalendar, FiPieChart } from 'react-icons/fi';
import { Badge } from '../components/ui/badge';
import municipios from '../data/municipios.json';

const INFRA_FORM_TYPES = ['infraestrutura-economica', 'infraestrutura-economica-urbana'];

const FORM_TYPE_FILTERS = {
  all: 'all',
  'desenvolvimento-produtivo': ['desenvolvimento-produtivo'],
  'infraestrutura-economica': INFRA_FORM_TYPES,
  'diagnostico-municipal': ['diagnostico-municipal']
};

const FORM_TYPE_LABELS = {
  'desenvolvimento-produtivo': 'Desenvolvimento Produtivo',
  'infraestrutura-economica': 'Infraestrutura Econômica',
  'infraestrutura-economica-urbana': 'Infraestrutura Econômica',
  'diagnostico-municipal': 'Diagnóstico Municipal',
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState([]);
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeStatusFilter, setActiveStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' ou 'table'

  useEffect(() => {
    const fetchResponses = async () => {
      setLoading(true);
      try {
        const { success, data, error } = await getAllFormResponses();
        
        if (!success) {
          throw new Error(error || 'Erro ao buscar respostas');
        }
        
        // Extrair lista de usuários únicos para o filtro
        const users = data
          .filter(response => response.user)
          .map(response => ({
            id: response.user.id,
            name: response.user.name || response.user.email || 'N/A'
          }));
        
        // Remover duplicatas baseado no ID do usuário
        const uniqueUsersMap = new Map();
        users.forEach(user => uniqueUsersMap.set(user.id, user));
        setUniqueUsers(Array.from(uniqueUsersMap.values()));
        
        setResponses(data || []);
        setFilteredResponses(data || []);
      } catch (err) {
        console.error('Error fetching responses:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchResponses();
  }, []);

  const applyFilters = (formTypeFilter, statusFilter, userFilterValue) => {
    let filtered = [...responses];
    
    // Aplicar filtro de tipo de formulário
    if (formTypeFilter !== 'all') {
      const formTypes = Array.isArray(formTypeFilter) ? formTypeFilter : [formTypeFilter];
      filtered = filtered.filter(response => formTypes.includes(response.form_type));
    }
    
    // Aplicar filtro de status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(response => (response.status || 'draft') === statusFilter);
    }
    
    // Aplicar filtro de usuário
    if (userFilterValue) {
      filtered = filtered.filter(response => {
        const userName = response.user?.name || response.user?.email || '';
        return userName.toLowerCase().includes(userFilterValue.toLowerCase());
      });
    }
    
    return filtered;
  };

  const handleFilterChange = (filterKey) => {
    setActiveFilter(filterKey);
    setCurrentPage(1); // Reset to first page when filter changes
    const selectedFormType = FORM_TYPE_FILTERS[filterKey] ?? 'all';
    setFilteredResponses(applyFilters(selectedFormType, activeStatusFilter, userFilter));
  };
  
  const handleStatusFilterChange = (statusFilter) => {
    setActiveStatusFilter(statusFilter);
    setCurrentPage(1); // Reset to first page when filter changes
    const selectedFormType = FORM_TYPE_FILTERS[activeFilter] ?? 'all';
    setFilteredResponses(applyFilters(selectedFormType, statusFilter, userFilter));
  };
  
  const handleUserFilterChange = (event) => {
    const value = event.target.value;
    setUserFilter(value);
    setCurrentPage(1); // Reset to first page when filter changes
    const selectedFormType = FORM_TYPE_FILTERS[activeFilter] ?? 'all';
    setFilteredResponses(applyFilters(selectedFormType, activeStatusFilter, value));
  };

  const exportToCSV = () => {
    // Exporta cada campo do JSON responses em uma coluna separada
    if (filteredResponses.length === 0) return;

    // 1. Descobrir todos os campos únicos presentes em responses
    const allJsonKeys = new Set();
    filteredResponses.forEach(response => {
      if (response.responses && typeof response.responses === 'object') {
        Object.keys(response.responses).forEach(key => allJsonKeys.add(key));
      }
    });
    const jsonKeys = Array.from(allJsonKeys).sort((a, b) => a.localeCompare(b));

    // 2. Cabeçalho dinâmico (campos do JSON em ordem alfabética)
    const headers = ['ID', 'Tipo de Formulário', 'Usuário', 'Status', 'Data de Atualização', ...jsonKeys];

    // Função para escapar corretamente valores para CSV
    const escapeCSV = (value) => {
      if (value === null || value === undefined) {
        return '""'; // Retornar aspas vazias para valores nulos
      }
      
      // Converter para string se não for
      let stringValue = String(value);
      
      // Substituir aspas duplas por aspas duplas duplicadas (padrão RFC4180)
      stringValue = stringValue.replace(/"/g, '""');
      
      // Sempre envolver em aspas para garantir que quebras de linha e vírgulas sejam preservadas
      return `"${stringValue}"`;
    };

    // Função para tratar objetos e arrays
    const handleComplexValue = (value) => {
      if (value === null || value === undefined) {
        return '""';
      }
      
      // Normalizar quebras de linha para evitar problemas de formatação em campos de texto
      if (typeof value === 'string') {
        // Substituir múltiplas quebras de linha por espaços para evitar quebrar o CSV
        const normalizedValue = value.replace(/\r\n|\r|\n/g, ' ');
        return escapeCSV(normalizedValue);
      }
      
      if (typeof value === 'object') {
        try {
          // Para arrays simples, criar uma representação mais amigável usando ponto-e-vírgula
          if (Array.isArray(value)) {
            if (value.every(item => typeof item === 'string' || typeof item === 'number')) {
              return escapeCSV(value.join('; '));
            }
          }
          
          // Para objetos ou arrays complexos, serializar como JSON
          const jsonString = JSON.stringify(value, (_, v) => {
            if (v === undefined) return null;
            return v;
          });
          
          // Garantir que o JSON serializado não quebre o CSV
          return escapeCSV(jsonString);
        } catch (error) {
          console.error('Erro ao serializar objeto:', error);
          return escapeCSV('[objeto complexo]');
        }
      }
      
      return escapeCSV(value);
    };

    // 3. Montar linhas
    const rows = filteredResponses.map(response => {
      // Campos base
      const baseFields = [
        escapeCSV(response.id),
        escapeCSV(response.form_type),
        escapeCSV(response.user?.name || response.user?.email || 'N/A'),
        escapeCSV(response.status || 'draft'),
        escapeCSV(new Date(response.updated_at || response.created_at).toLocaleDateString('pt-BR'))
      ];
      
      // Campos do JSON
      const jsonFields = jsonKeys.map(key => {
        const value = response.responses && response.responses[key] !== undefined 
          ? response.responses[key] 
          : '';
        return handleComplexValue(value);
      });
      
      return [...baseFields, ...jsonFields].join(';');
    });

    // Juntar cabeçalho e linhas
    const csvContent = [
      headers.map(escapeCSV).join(';'),
      ...rows
    ].join('\r\n'); // Usar CRLF para melhor compatibilidade

    // 4. Criar e baixar o arquivo com BOM para suporte a caracteres especiais
    const BOM = '\uFEFF'; // Byte Order Mark para UTF-8
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `respostas-${activeFilter}-${new Date().toISOString().slice(0, 10)}.csv`);
    
    // Adicionar feedback visual para o usuário
    const toast = document.createElement('div');
    toast.style.position = 'fixed';
    toast.style.bottom = '20px';
    toast.style.right = '20px';
    toast.style.backgroundColor = '#4CAF50';
    toast.style.color = 'white';
    toast.style.padding = '16px';
    toast.style.borderRadius = '4px';
    toast.style.zIndex = '1000';
    toast.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    toast.textContent = 'Arquivo CSV exportado com sucesso!';
    document.body.appendChild(toast);
    
    // Remover o toast após 3 segundos
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.5s';
      setTimeout(() => {
        if (toast.parentNode) {
          document.body.removeChild(toast);
        }
      }, 500);
    }, 3000);
    
    // Iniciar o download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Liberar memória
  };

  // Análises quantitativas avançadas
  const analytics = useMemo(() => {
    if (responses.length === 0) return null;

    // Filtrar apenas respostas enviadas para análises
    const respostasEnviadas = responses.filter(r => r.status === 'submitted');
    const totalRespostas = responses.length;
    const totalEnviadas = respostasEnviadas.length;
    const usuariosUnicos = [...new Set(responses.map(r => r.user_id))].length;
    const taxaConclusao = ((totalEnviadas / totalRespostas) * 100).toFixed(1);

    // Análise temporal (apenas enviadas)
    const respostasPorMes = {};
    respostasEnviadas.forEach(r => {
      const data = new Date(r.created_at);
      const mesAno = `${data.getMonth() + 1}/${data.getFullYear()}`;
      respostasPorMes[mesAno] = (respostasPorMes[mesAno] || 0) + 1;
    });

    // Análise geográfica (municípios mais ativos - apenas enviadas)
    const municipiosCount = {};
    respostasEnviadas.forEach(r => {
      if (r.responses?.nome_municipio) {
        const mun = r.responses.nome_municipio;
        municipiosCount[mun] = (municipiosCount[mun] || 0) + 1;
      }
    });
    const topMunicipios = Object.entries(municipiosCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Análise por tipo de formulário (apenas enviadas)
    const porTipo = {
      'desenvolvimento-produtivo': respostasEnviadas.filter(r => r.form_type === 'desenvolvimento-produtivo').length,
      'infraestrutura-economica': respostasEnviadas.filter(r => INFRA_FORM_TYPES.includes(r.form_type)).length,
      'diagnostico-municipal': respostasEnviadas.filter(r => r.form_type === 'diagnostico-municipal').length
    };

    // Análise de setores econômicos (apenas enviadas)
    const setoresCount = {};
    respostasEnviadas.forEach(r => {
      if (r.responses?.setores_economicos) {
        const setores = Array.isArray(r.responses.setores_economicos) 
          ? r.responses.setores_economicos 
          : [r.responses.setores_economicos];
        setores.forEach(setor => {
          if (setor && setor !== '') {
            setoresCount[setor] = (setoresCount[setor] || 0) + 1;
          }
        });
      }
    });
    const topSetores = Object.entries(setoresCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    // Análise de programas e políticas públicas (apenas enviadas)
    const programasCount = respostasEnviadas.filter(r => {
      return r.responses?.programa_nome_1 || 
             r.responses?.programa_nome_2 || 
             r.responses?.programa_nome_3;
    }).length;

    const aplsCount = respostasEnviadas.filter(r => 
      r.responses?.presenca_apl === 'Sim' || r.responses?.presenca_apl === 'sim'
    ).length;

    // Média de campos preenchidos por resposta (apenas enviadas)
    const camposPreenchidos = respostasEnviadas.map(r => {
      if (!r.responses) return 0;
      return Object.values(r.responses).filter(v => v !== null && v !== undefined && v !== '').length;
    });
    const mediaCampos = camposPreenchidos.length > 0 
      ? (camposPreenchidos.reduce((a, b) => a + b, 0) / camposPreenchidos.length).toFixed(1)
      : 0;

    return {
      totalRespostas,
      totalEnviadas,
      usuariosUnicos,
      taxaConclusao,
      respostasPorMes,
      topMunicipios,
      porTipo,
      topSetores,
      programasCount,
      aplsCount,
      mediaCampos
    };
  }, [responses]);

  // Estatísticas para o dashboard
  const stats = [
    {
      title: 'Total de Respostas',
      value: responses.length,
      icon: <FiFileText className="h-6 w-6 text-primary-600" />,
      color: 'bg-primary-50',
      description: `${analytics?.totalEnviadas || 0} enviadas`
    },
    {
      title: 'Usuários Únicos',
      value: [...new Set(responses.map(r => r.user_id))].length,
      icon: <FiUsers className="h-6 w-6 text-secondary-600" />,
      color: 'bg-secondary-50',
      description: `${analytics?.taxaConclusao || 0}% taxa de conclusão`
    },
    {
      title: 'Desenvolvimento Produtivo',
      value: responses.filter(r => r.form_type === 'desenvolvimento-produtivo').length,
      icon: <FiBarChart2 className="h-6 w-6 text-green-600" />,
      color: 'bg-green-50',
      description: `${analytics?.programasCount || 0} com programas`
    },
    {
      title: 'Infraestrutura Econômica',
      value: responses.filter(r => INFRA_FORM_TYPES.includes(r.form_type)).length,
      icon: <FiBarChart2 className="h-6 w-6 text-blue-600" />,
      color: 'bg-blue-50',
      description: `${analytics?.aplsCount || 0} com APLs`
    },
    {
      title: 'Diagnóstico Municipal',
      value: responses.filter(r => r.form_type === 'diagnostico-municipal').length,
      icon: <FiBarChart2 className="h-6 w-6 text-purple-600" />,
      color: 'bg-purple-50',
      description: `${analytics?.porTipo?.['diagnostico-municipal'] || 0} enviadas`
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        <p>Erro ao carregar os dados: {error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Dashboard</h1>
            <p className="mt-2 text-gray-600">
              Visualize e analise os dados coletados pelos formulários do Observatório Socioeconômico e Ambiental da RIDE-DF.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('overview')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'overview'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <FiPieChart className="inline-block mr-1 h-4 w-4" />
              Visualização
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <FiFileText className="inline-block mr-1 h-4 w-4" />
              Tabela
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className={`${stat.color} overflow-hidden rounded-lg shadow`}>
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {stat.icon}
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.title}
                  </dt>
                  <dd className="flex flex-col">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </div>
                    {stat.description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {stat.description}
                      </div>
                    )}
                  </dd>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Visualização Quantitativa */}
      {viewMode === 'overview' && analytics && (
        <>
        

          {/* Análise de Setores Econômicos */}
          <div className="bg-white shadow rounded-lg mb-8 p-6">
            <div className="flex items-center mb-4">
              <FiTrendingUp className="h-5 w-5 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Setores Econômicos Predominantes</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Principais setores econômicos identificados nas respostas
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analytics.topSetores.map(([setor, count]) => {
                const maxCount = analytics.topSetores[0]?.[1] || 1;
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={setor} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700 flex-1">{setor}</span>
                      <span className="text-lg font-bold text-primary-600 ml-2">{count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
            {analytics.topSetores.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">Nenhum setor econômico informado</p>
            )}
          </div>

          {/* Análise Temporal e Estatísticas Adicionais */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Evolução Temporal */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center mb-4">
                <FiCalendar className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Evolução Temporal</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Respostas coletadas por mês
              </p>
              <div className="space-y-3">
                {Object.entries(analytics.respostasPorMes)
                  .sort((a, b) => {
                    const [mesA, anoA] = a[0].split('/').map(Number);
                    const [mesB, anoB] = b[0].split('/').map(Number);
                    return anoA !== anoB ? anoA - anoB : mesA - mesB;
                  })
                  .slice(-6)
                  .map(([mesAno, count]) => {
                    const maxCount = Math.max(...Object.values(analytics.respostasPorMes));
                    const percentage = (count / maxCount) * 100;
                    return (
                      <div key={mesAno} className="flex items-center">
                        <div className="w-16 text-sm font-medium text-gray-500">{mesAno}</div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-700 font-medium w-8 text-right">{count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Estatísticas Adicionais */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center mb-4">
                <FiBarChart2 className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Estatísticas Adicionais</h3>
              </div>
              <div className="space-y-4">
                <div className="border-b border-gray-200 pb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Média de campos preenchidos</span>
                    <span className="text-2xl font-bold text-gray-900">{analytics.mediaCampos}</span>
                  </div>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Formulários com programas públicos</span>
                    <span className="text-2xl font-bold text-green-600">{analytics.programasCount}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {((analytics.programasCount / analytics.totalRespostas) * 100).toFixed(1)}% do total
                  </div>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Municípios com APLs identificados</span>
                    <span className="text-2xl font-bold text-blue-600">{analytics.aplsCount}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {((analytics.aplsCount / analytics.totalRespostas) * 100).toFixed(1)}% do total
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Taxa de conclusão</span>
                    <span className="text-2xl font-bold text-primary-600">{analytics.taxaConclusao}%</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {analytics.totalEnviadas} de {analytics.totalRespostas} enviadas
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Filters and Export */}
      {viewMode === 'table' && (
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-medium text-gray-700">Filtros</h3>
            <button 
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="inline-flex items-center px-2 py-1 text-sm text-gray-600 hover:text-gray-900 focus:outline-none"
              aria-expanded={filtersExpanded}
              aria-label={filtersExpanded ? 'Recolher filtros' : 'Expandir filtros'}
            >
              {filtersExpanded ? (
                <>
                  <span className="mr-1"></span>
                  <FiChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  <span className="mr-1"></span>
                  <FiChevronDown className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
          
          <div className={`flex flex-col space-y-4 ${filtersExpanded ? '' : 'hidden'}`}>
            {/* Tipo de Formulário */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Tipo de Formulário</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFilterChange('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeFilter === 'all'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <FiFilter className="inline-block mr-1 h-4 w-4" />
                  Todos
                </button>
                <button
                  onClick={() => handleFilterChange('desenvolvimento-produtivo')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeFilter === 'desenvolvimento-produtivo'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Desenvolvimento Produtivo
                </button>
                <button
                  onClick={() => handleFilterChange('infraestrutura-economica')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeFilter === 'infraestrutura-economica'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Infraestrutura Econômica
                </button>
                <button
                  onClick={() => handleFilterChange('diagnostico-municipal')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeFilter === 'diagnostico-municipal'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Diagnóstico Municipal
                </button>
              </div>
            </div>
            
            {/* Status do Formulário */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Status do Formulário</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleStatusFilterChange('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeStatusFilter === 'all'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <FiFilter className="inline-block mr-1 h-4 w-4" />
                  Todos
                </button>
                <button
                  onClick={() => handleStatusFilterChange('draft')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeStatusFilter === 'draft'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Rascunho
                </button>
                <button
                  onClick={() => handleStatusFilterChange('submitted')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeStatusFilter === 'submitted'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Enviado
                </button>
{/*                 <button
                  onClick={() => handleStatusFilterChange('review')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeStatusFilter === 'review'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Em Revisão
                </button>
                <button
                  onClick={() => handleStatusFilterChange('approved')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeStatusFilter === 'approved'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Aprovado
                </button>
                <button
                  onClick={() => handleStatusFilterChange('rejected')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    activeStatusFilter === 'rejected'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Rejeitado
                </button> */}
              </div>
            </div>
            
            {/* Filtro por Usuário */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Filtrar por Usuário</h4>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUsers className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={userFilter}
                  onChange={handleUserFilterChange}
                  placeholder="Digite o nome do usuário..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {userFilter && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      onClick={() => handleUserFilterChange({ target: { value: '' } })}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <span className="sr-only">Limpar filtro</span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Botão de exportação removido daqui e movido para o cabeçalho da tabela */}
          </div>
        </div>
      </div>
      )}

      {/* Responses Table */}
      {viewMode === 'table' && (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Respostas dos Formulários
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {filteredResponses.length} resultados encontrados
              </p>
            </div>
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={filteredResponses.length === 0}
            >
              <FiDownload className="mr-2 h-4 w-4" />
              Exportar CSV
            </button>
          </div>
        </div>
        
        {filteredResponses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo de Formulário
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Atualização
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResponses
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((response) => (
                  <tr key={response.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {response.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {FORM_TYPE_LABELS[response.form_type] || response.form_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {response.user?.name || response.user?.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <StatusBadge status={response.status || 'draft'} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(response.updated_at || response.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredResponses.length / itemsPerPage)))}
                  disabled={currentPage >= Math.ceil(filteredResponses.length / itemsPerPage)}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredResponses.length)}</span> a <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredResponses.length)}</span> de <span className="font-medium">{filteredResponses.length}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Anterior</span>
                      <FiChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    {Array.from({ length: Math.min(5, Math.ceil(filteredResponses.length / itemsPerPage)) }, (_, i) => {
                      // Logic to show pages around current page
                      const totalPages = Math.ceil(filteredResponses.length / itemsPerPage);
                      let pageNum;
                      
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum ? 'z-10 bg-primary-50 border-primary-500 text-primary-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredResponses.length / itemsPerPage)))}
                      disabled={currentPage >= Math.ceil(filteredResponses.length / itemsPerPage)}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Próximo</span>
                      <FiChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 py-5 sm:px-6 text-center text-gray-500">
            Nenhum resultado encontrado para o filtro selecionado.
          </div>
        )}
      </div>
      )}
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: 'Rascunho', variant: 'secondary' },
    submitted: { label: 'Enviado', variant: 'success' },
    approved: { label: 'Aprovado', variant: 'success' },
    rejected: { label: 'Rejeitado', variant: 'destructive' },
    review: { label: 'Em Revisão', variant: 'warning' }
  };

  const config = statusConfig[status] || { label: status, variant: 'outline' };

  return (
    <Badge variant={config.variant}>
      {config.label}
    </Badge>
  );
};

export default Dashboard;
